import React, { useState } from "react";
import {
  alpha,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const BLUE = "#00336C";
const PLACEHOLDER_IMAGE = "/images/property-placeholder.svg";

export default function PropertyImageGallery({ images = [], propertyName }) {
  const gallery = images.length ? images : [PLACEHOLDER_IMAGE];
  const [selectedIndex, setSelectedIndex] = useState(null);
  const showPrevious = () => setSelectedIndex((current) => (current - 1 + gallery.length) % gallery.length);
  const showNext = () => setSelectedIndex((current) => (current + 1) % gallery.length);

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gallery.length > 1 ? { xs: "1fr", md: "2fr 1fr" } : "1fr",
          gap: 1,
          p: 1,
          bgcolor: alpha(BLUE, 0.035),
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => setSelectedIndex(0)}
          aria-label={`Enlarge primary photo of ${propertyName}`}
          sx={{ p: 0, border: 0, cursor: "zoom-in", bgcolor: "transparent", overflow: "hidden", borderRadius: 2 }}
        >
          <Box component="img" src={gallery[0]} alt={`${propertyName} primary`} sx={{ display: "block", width: "100%", height: { xs: 180, md: 240 }, objectFit: "cover" }} />
        </Box>
        {gallery.length > 1 && (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, 1fr)", md: "1fr 1fr" }, gap: 1, maxHeight: { md: 240 }, overflowY: "auto" }}>
            {gallery.slice(1).map((image, index) => (
              <Box
                key={`${image.slice(0, 24)}-${index}`}
                component="button"
                type="button"
                onClick={() => setSelectedIndex(index + 1)}
                aria-label={`Enlarge photo ${index + 2} of ${propertyName}`}
                sx={{ position: "relative", p: 0, border: 0, cursor: "zoom-in", bgcolor: "transparent", overflow: "hidden", borderRadius: 2 }}
              >
                <Box component="img" src={image} alt={`${propertyName} ${index + 2}`} sx={{ display: "block", width: "100%", height: { xs: 84, md: "100%" }, minHeight: { md: 113 }, objectFit: "cover" }} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Dialog
        open={selectedIndex !== null}
        onClose={() => setSelectedIndex(null)}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") showPrevious();
          if (event.key === "ArrowRight") showNext();
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#0b1220" } }}
      >
        <IconButton aria-label="Close image viewer" onClick={() => setSelectedIndex(null)} sx={{ position: "absolute", right: 10, top: 10, zIndex: 2, color: "white", bgcolor: "rgba(0,0,0,.45)" }}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ position: "relative", p: 1, display: "grid", placeItems: "center", minHeight: { xs: 360, sm: 500 } }}>
          {selectedIndex !== null && <Box component="img" src={gallery[selectedIndex]} alt={`${propertyName} photo ${selectedIndex + 1} of ${gallery.length}`} sx={{ maxWidth: "100%", maxHeight: "82vh", objectFit: "contain" }} />}
          {gallery.length > 1 && selectedIndex !== null && (
            <>
              <IconButton aria-label="Previous property image" onClick={showPrevious} sx={{ position: "absolute", left: { xs: 8, sm: 18 }, top: "50%", transform: "translateY(-50%)", color: "white", bgcolor: "rgba(0,0,0,.5)", "&:hover": { bgcolor: "rgba(0,0,0,.72)" } }}>
                <ChevronLeftIcon />
              </IconButton>
              <IconButton aria-label="Next property image" onClick={showNext} sx={{ position: "absolute", right: { xs: 8, sm: 18 }, top: "50%", transform: "translateY(-50%)", color: "white", bgcolor: "rgba(0,0,0,.5)", "&:hover": { bgcolor: "rgba(0,0,0,.72)" } }}>
                <ChevronRightIcon />
              </IconButton>
              <Typography sx={{ position: "absolute", left: "50%", bottom: 14, transform: "translateX(-50%)", px: 1.25, py: 0.4, borderRadius: 5, color: "white", bgcolor: "rgba(0,0,0,.62)", fontSize: 12, fontWeight: 700 }}>
                {selectedIndex + 1} / {gallery.length}
              </Typography>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
